import * as React from 'react';
import * as _ from 'underscore';
import { DataGrid, IndexBasedGridSelection, SelectionBehavior } from '@tableau/widgets-datagrid';
import { ActivitySpinnerWidget } from '@tableau/widgets';
import { CheckboxState } from '../../../common/components/checkboxes/CheckboxState';
import { CommonColumn } from '../../../app/spaces/ListView/types/Columns';
import {
  CenterAlignedCell,
  RightAlignedCell,
  RightAlignedHeader
} from '../../../app/spaces/ListView/StyledCellWrapper';
import { SortableHeader } from '../../../app/spaces/ListView/SortableHeader';
import { GroupDataGridCells } from '../../../app/spaces/groups/GroupDataGridCells';
import { SiteGridCells } from '../../../app/spaces/ServerSpaces/Sites/SiteGridCells';
import { Messages } from '../../../codegen/Localize';
import { IGroupInfo } from '../../../ts/models/IGroupInfo';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { GroupsGridHeaderFormatterFactory } from './GroupsGridHeaderFormatter';
import { GroupsGridCellFormatterFactory } from './GroupsGridCellFormatter';
import { dataGridContainerStyle, dataGridStyle } from './styles/EditGroupMembershipDialogStyles';
import { LocalizedCountCell } from '../../../app/spaces/ListView/dataGridCells/PlainTextAndNumberCells';

export const GROUPS_GRID_NO_RESULTS_TEXT_TESTID = 'groups-data-grid-empty';
export const GROUPS_GRID_LOADING_GROUPS_SPINNER_TESTID = 'groups-grid-loading-groups';

export interface GroupsGridRow extends DataGrid.Row {
}

export interface GroupsGridColumn extends DataGrid.Column {
  checkboxState?: CheckboxState;
  isCheckboxEnabled?: boolean;
  onCheckboxChanged?: () => void;
}

export interface GroupsGridCheckboxCellData {
  checkboxState: CheckboxState;
  isCheckboxEnabled: boolean;
  onCheckboxChanged: () => void;
}

export interface GroupsGridGroupCellData {
  groupName: string;
  domainName: string;
}

interface GroupsGridRowAndColumnInfo {
  cols: DataGrid.Column[];
  rows: DataGrid.Row[];
  rowStore: DataGrid.RowStore;
}

export type GroupsGridProps = {
  awaitDisplayData: boolean;
  currentColumnSortOrder: IOrderClause;
  displayData: IGroupInfo[];
  handleRowCheckboxChange: (rowData: Partial<IGroupInfo>) => void;
  handleColumnCheckboxChange: () => void;
  handleColumnSortOrderChange: (field: string) => void;
  headerCheckboxState: CheckboxState;
  rowCheckboxState: _.Dictionary<string>;
  canUserManageGroup: (rowData: IGroupInfo) => boolean;
};

export type GroupsGridState = {
};

export enum GroupsGridColumnKeys {
  Id = 'id',
  Checkbox = 'checkbox',
  GroupName = 'name',
  UserCount = 'userCount',
  MinimumSiteRole = 'minimumSiteRole',
  GrantLicenseMode = 'grantLicenseMode',
  DomainName = 'domainName'
}

export class GroupsGrid extends React.Component<GroupsGridProps, GroupsGridState> {
  private readonly minColWidth: number = 32;
  private readonly selectionModel: IndexBasedGridSelection = new IndexBasedGridSelection(0, 0);

  constructor(props: GroupsGridProps) {
    super(props);
    this.selectionModel.selectionBehavior = SelectionBehavior.None;
  }

  private createGroupsGridRowAndColumnInfo(): GroupsGridRowAndColumnInfo {
    let cols = this.generateColumns();
    let rows = this.generateRows();
    let rowStore = this.generateRowStore(rows);
    let groupsGridRowAndColumnInfo = {rows: rows, cols: cols, rowStore: rowStore};
    return groupsGridRowAndColumnInfo;
  }

  private generateColumns(): DataGrid.Column[] {
    const sortableHeaderFormatter = SortableHeader.Formatter([this.props.currentColumnSortOrder], this.props.handleColumnSortOrderChange);
    const isHeaderCheckboxEnabled = (this.props.displayData.length !== 0 && !(this.props.displayData.length === 1 && this.props.displayData[0].isAllUsersGroup));
    const columns: GroupsGridColumn[] = [];
    columns.push({
      name: CommonColumn.Select,
      dataKey: GroupsGridColumnKeys.Checkbox,
      headerText: '',
      resizable: false,
      sortable: false,
      checkboxState: this.props.headerCheckboxState,
      isCheckboxEnabled: isHeaderCheckboxEnabled,
      onCheckboxChanged: () => this.props.handleColumnCheckboxChange(),
      headerFormatter: GroupsGridHeaderFormatterFactory,
      cellFormatter: GroupsGridCellFormatterFactory
    });
    columns.push({
      name: CommonColumn.GroupName,
      dataKey: GroupsGridColumnKeys.GroupName,
      sortable: true,
      headerText: Messages.group(),
      direction: 'asc',
      growToFit: true,
      headerFormatter: sortableHeaderFormatter,
      cellFormatter: GroupsGridCellFormatterFactory
    });
    columns.push({
      name: CommonColumn.GroupUserCount,
      dataKey: GroupsGridColumnKeys.UserCount,
      sortable: true,
      headerText: Messages.members(),
      direction: 'asc',
      headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
      cellFormatter: RightAlignedCell(LocalizedCountCell(GroupsGridColumnKeys.UserCount))
    });
    columns.push({
      name: CommonColumn.GroupMinSiteRole,
      dataKey: GroupsGridColumnKeys.MinimumSiteRole,
      sortable: true,
      headerText: Messages.column_minimumSiteRole(),
      direction: 'asc',
      headerFormatter: sortableHeaderFormatter,
      cellFormatter: GroupDataGridCells.MinimumSiteRoleCell()
    });
    columns.push({
      name: CommonColumn.GroupLicenseOnSignIn,
      dataKey: GroupsGridColumnKeys.GrantLicenseMode,
      sortable: true,
      headerText: Messages.column_license_on_signin(),
      direction: 'asc',
      headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
      cellFormatter: CenterAlignedCell(SiteGridCells.LicenseOnSignInFormatter)
    });
    return columns;
  }

  private generateRows(): DataGrid.Row[] {
    let rows: GroupsGridRow[] = [];
    let nextRowIndex: number = 0;
    this.props.displayData.forEach(rowData => {
      let rowItemData: { [key: string]: {} } = {};
      const currentRowIndex = nextRowIndex++;
      const rowCheckboxState = this.props.rowCheckboxState[rowData.id];
      let checkboxCellData: GroupsGridCheckboxCellData = {
        checkboxState: rowCheckboxState,
        isCheckboxEnabled: !rowData.isAllUsersGroup && this.props.canUserManageGroup(rowData),
        onCheckboxChanged: () => { this.props.handleRowCheckboxChange(rowData); }
      };

      let groupCellData: GroupsGridGroupCellData = {
        groupName: rowData.name,
        domainName: rowData.domainName
      };
      rowItemData[GroupsGridColumnKeys.Checkbox] = checkboxCellData;
      rowItemData[GroupsGridColumnKeys.GroupName] = groupCellData;
      rowItemData[GroupsGridColumnKeys.UserCount] = rowData.userCount;
      rowItemData[GroupsGridColumnKeys.MinimumSiteRole] = rowData.minimumSiteRole;
      rowItemData[GroupsGridColumnKeys.GrantLicenseMode] = rowData.grantLicenseMode;
      rowItemData[GroupsGridColumnKeys.DomainName] = rowData.domainName;
      rows.push({
        key: (currentRowIndex).toString(),
        data: rowItemData
      });
    });

    return rows;
  }

  private generateRowStore(rows: DataGrid.Row[]): DataGrid.RowStore {
    return {
      getItems: (start: number, end: number, rowHandler: (rows: DataGrid.Row[]) => void) => {
        rowHandler(rows);
      }
    };
  }

  private handleSelectionEvent = (event: DataGrid.CellSelectionEvent): boolean => {
    return false;
  }

  private emptyChild = (): JSX.Element => {
    return (
      <div className={'tb-data-grid-empty'}>
        <div className={'tb-data-grid-empty-title'} data-tb-test-id={GROUPS_GRID_NO_RESULTS_TEXT_TESTID}>{Messages.noMatchingResults()}</div>
      </div>
    );
  }

  private spinnerChild = (): JSX.Element => {
    return (
      <div className={'tb-data-grid-empty'}>
        <ActivitySpinnerWidget testId={GROUPS_GRID_LOADING_GROUPS_SPINNER_TESTID} shouldShowUnderlay={false}/>
      </div>
    );
  }

  public render(): JSX.Element {
    const rowAndColumnDataGridInfo = this.createGroupsGridRowAndColumnInfo();

    const dataGridProps: DataGrid.Props = {
      cols: rowAndColumnDataGridInfo.cols,
      config: {
        rowOuterHeight: this.minColWidth,
        rememberLastScrollPos: true
      },
      emptyChild: this.props.awaitDisplayData? this.spinnerChild() : this.emptyChild(),
      onSelectionEvent: this.handleSelectionEvent,
      rowStore: rowAndColumnDataGridInfo.rowStore,
      selectionModel: this.selectionModel,
      showHeaders: true,
      showVerticalGridLines: false,
      showHorizontalGridLines: false,
      showRowBanding: false,
      style: dataGridStyle
    };

    return (
      <div className={dataGridContainerStyle}>
        <div className={'tb-spaces-list-view'} data-tb-test-id="tb-groups-data-grid">
        <DataGrid
          {...dataGridProps}
        />
        </div>
      </div>
    );
  }
}
